import { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getProduct } from '../../../features/products/productsSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faSpinner, faFilter } from '@fortawesome/free-solid-svg-icons';
import CategoryDropdown from '../../search/CategoryDropdown';
import SuggestionList from '../../search/SuggestionList';
import { getSearchCategories, getSearchSuggestions } from '../../../utils/searchApi';
import PlaceholdersAndVanishInput from '../../../utils/ui/placeholders-and-vanish-input';

const DEBOUNCE_MS = 300;


const PLACEHOLDERS = [
  'Search sunglasses…',
  'Search spectacle frames…',
  'Search contact lenses…',
  'Search kids frames…',
  'Search by brand or product name…',
];


const toFilterItem = (id, name, list) => {
  if (id === undefined || id === null || id === '') return null;
  const resolved =
    name || list?.find((x) => String(x?.id) === String(id))?.name || '';
  return { id, name: resolved };
};

const getItemFilters = (item, lists = {}) => ({
  category: toFilterItem(
    item?.category_id ?? item?.p_category_id ?? item?.p_category?.id ?? item?.category?.id,
    item?.category_name ?? item?.p_category?.name ?? item?.category?.name,
    lists.categories,
  ),
  gender: toFilterItem(
    item?.gender_id ?? item?.gender?.id,
    item?.gender_name ?? item?.gender?.name,
    lists.gender,
  ),
  brands: toFilterItem(
    item?.brand_id ?? item?.Brand?.id ?? item?.brand?.id,
    item?.brand_name ?? item?.Brand?.name ?? item?.brand?.name,
    lists.brands,
  ),
  frameShape: toFilterItem(
    item?.shape_id ?? item?.frame_shape_id ?? item?.shape?.id ?? item?.frame_shape?.id,
    item?.shape_name ?? item?.shape?.name ?? item?.frame_shape?.name,
    lists.shape,
  ),
  material: toFilterItem(
    item?.material_id ?? item?.material?.id,
    item?.material_name ?? item?.material?.name,
    lists.material,
  ),
  frameType: toFilterItem(
    item?.frame_type_id ?? item?.frame_type?.id ?? item?.frameType?.id,
    item?.frame_type_name ?? item?.frame_type?.name ?? item?.frameType?.name,
    lists.frameType,
  ),
  
  modality: item?.modality
    ? { id: item.modality, name: item.modality }
    : null,
  colour: toFilterItem(
    item?.colour_id ?? item?.color_id ?? item?.colour?.id ?? item?.color?.id,
    item?.colour_name ?? item?.color_name ?? item?.colour?.name ?? item?.color?.name,
    lists.colour,
  ),
});

export default function Search_products({
  onClick,
  onOpenProduct,
  filters,
  onFilterChange,
  onCategorySelect,
  onProductSelect, 
}) {
  const dispatch = useDispatch();

  // master lists, used to fill in chip names when only an id is available
  const masterCategories = useSelector((state) => state.master.categories);
  const masterGender = useSelector((state) => state.master.all_gender);
  const masterMaterial = useSelector((state) => state.master.all_material);
  const masterFrameType = useSelector((state) => state.master.all_frame_type);
  const masterColour = useSelector((state) => state.master.all_color);
  const masterBrands = useSelector((state) => state.master.brands);
  const masterShape = useSelector((state) => state.master.all_shape);

  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState('all');
  const [keyword, setKeyword] = useState('');
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null); 
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);
  const vanishRef = useRef(null);
  const submittedRef = useRef(false); 

  useEffect(() => {
    getSearchCategories()
      .then(setCategories)
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

 
  const categoryFilter = filters?.category;
  useEffect(() => {
    if (Array.isArray(categoryFilter) && categoryFilter.length === 1) {
      setCategoryId(String(categoryFilter[0].id));
    } else {
      setCategoryId('all');
    }
  }, [categoryFilter]);

 
  const filterName = filters?.name;
  useEffect(() => {
    if (!filterName && submittedRef.current) {
      submittedRef.current = false;
      setKeyword('');
      setSelectedProductId(null);
      setSelectedItem(null);
    }
  }, [filterName]);

  const runSearch = useCallback((cat, kw) => {
    if (!kw || kw.trim().length === 0) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);

    getSearchSuggestions(cat, kw)
      .then((data) => {
        if (requestId !== requestIdRef.current) return;
        setSuggestions(data);
        setLoading(false);
        setHighlightedIndex(-1);
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        setSuggestions([]);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runSearch(categoryId, keyword);
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [keyword, categoryId, runSearch]);

  const handleCategoryChange = (id) => {
    setCategoryId(id);
    if (keyword.trim()) runSearch(id, keyword);

    const catObj = categories.find((c) => String(c.id) === String(id));
    onCategorySelect?.(id === 'all' ? null : catObj ? { id: catObj.id, name: catObj.name } : null);
  };

  const handleInputChange = (e) => {
    setKeyword(e.target.value);
    setSelectedProductId(null);
    setSelectedItem(null); // NEW
    setOpen(true);
  };

  const handleSelect = (item) => {
    
    const name = item?.name || item?.description || item?.product_name || item?.title || '';
    if (!name) return;
    setKeyword(name);
    setSelectedProductId(item?.id ?? null);
    setSelectedItem(item); // NEW
    setOpen(false);
  };

  const submitSearch = async () => {
    const kw = (keyword ?? '').trim();
    if (!kw) return;
    setOpen(false);

    vanishRef.current?.vanish();
    submittedRef.current = true;

    
    if (selectedItem && selectedProductId != null && onProductSelect) {
      
      let full = selectedItem;
      try {
        const res = await dispatch(getProduct(selectedProductId)).unwrap();
        full = { ...selectedItem, ...(res?.data ?? {}) };
      } catch (e) {
        
      }

      // console.log('full product used for filters:', full); // <- verify field names

      const itemFilters = getItemFilters(full, {
        categories: masterCategories?.length ? masterCategories : categories,
        gender: masterGender,
        material: masterMaterial,
        frameType: masterFrameType,
        colour: masterColour,
        brands: masterBrands,
        shape: masterShape,
      });

      
      setCategoryId(itemFilters.category ? String(itemFilters.category.id) : 'all');

      onProductSelect({
        productId: selectedProductId,
        name: kw,
        filters: itemFilters,
      });
      return;
    }

    
    onFilterChange?.('productId', '');
    onFilterChange?.('name', kw);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submitSearch();
      return;
    }

    if (!open || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const showPopup = open && keyword.trim().length > 0;

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
      <div style={{ display: 'flex', width: '100%', alignItems: 'stretch', boxSizing: 'border-box' }}>
        <CategoryDropdown categories={categories} value={categoryId} onChange={handleCategoryChange} />

        <PlaceholdersAndVanishInput
          ref={vanishRef}
          placeholders={PLACEHOLDERS}
          value={keyword}
          onChange={handleInputChange}
          onFocus={() => { if (keyword.trim()) setOpen(true); }}
          onKeyDown={handleKeyDown}
        >
          {loading && (
            <FontAwesomeIcon
              icon={faSpinner}
              spin
              style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94A3B8', fontSize: '14px', pointerEvents: 'none' }}
            />
          )}
        </PlaceholdersAndVanishInput>

        <button
          type="button"
          onClick={submitSearch}
          aria-label="Search"
          style={{
            boxSizing: 'border-box',
            width: '48px',
            height: '44px',
            flexShrink: 0,
            border: '1px solid #2563EB',
            borderLeft: 'none',
            borderRadius: '0 10px 10px 0',
            background: '#0dcaf0',
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#1D4ED8'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#2563EB'; }}
        >
          <FontAwesomeIcon icon={faSearch} style={{ fontSize: '14px' }} />
        </button>
      </div>

      {onClick && (
        <button
          type="button"
          onClick={onClick}
          className="d-lg-none"
          style={{
            boxSizing: 'border-box',
            height: '44px',
            padding: '0 16px',
            border: '1px solid #E2E8F0',
            borderRadius: '10px',
            background: '#FFFFFF',
            color: '#334155',
            fontSize: '14px',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexShrink: 0,
          }}
        >
          <FontAwesomeIcon icon={faFilter} />
          Filter
        </button>
      )}

      {showPopup && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          left: 0,
          right: onClick ? '124px' : 0,
          background: '#FFFFFF',
          border: '1px solid #E2E8F0',
          borderRadius: '14px',
          boxShadow: '0 20px 50px rgba(15,23,42,0.14)',
          zIndex: 70,
          overflow: 'hidden',
        }}>
          <SuggestionList
            suggestions={suggestions}
            loading={loading}
            keyword={keyword}
            highlightedIndex={highlightedIndex}
            onHover={setHighlightedIndex}
            onSelect={handleSelect}
          />
        </div>
      )}
    </div>
  );
}